import { EyeOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Grid, type TableColumnType, Tooltip } from 'antd';
import type { Log, LogFilter } from '@/features/log/types/Log';
import type { Filter } from '@/std/hooks/useFilter';
import { TableDefault } from '@/components/antd/TableDefault';
import { TableActionsInline } from '@/components/table/table-actions/TableActionsInline';
import { type ApiLogGetParams, apiLogGet } from '@/features/log/api/apiLogGet';
import { options } from '@/features/log/components/SelectAcao';
import { DATE_FORMAT_API } from '@/std/const/date';
import { usePagination } from '@/std/pagination/usePagination';
import { useSyncPaginationTotal } from '@/std/pagination/useSyncPaginationTotal';

const columns = ({
    open,
    showDetails,
}: {
    open: (row: Log) => void;
    showDetails: boolean;
}): TableColumnType<Log>[] => [
    {
        dataIndex: 'log_data_insercao',
        sorter: true,
        title: 'Data e Hora',
        width: '15%',
    },
    {
        dataIndex: 'log_tipo',
        hidden: !showDetails,
        render: (value: number) => options.find((o) => o.value === value)?.label ?? '',
        title: 'Ação',
        width: '15%',
    },
    {
        dataIndex: 'log_usuario_nome',
        title: 'Usuário',
        width: '20%',
    },
    {
        dataIndex: 'log_descricao',
        ellipsis: {
            showTitle: false,
        },

        hidden: !showDetails,
        render: (text: string) => {
            const formattedText = text?.replace(/\n/g, ' / ');

            return <Tooltip title={formattedText}>{formattedText}</Tooltip>;
        },
        title: 'Descrição',
        width: '40%',
    },
    {
        align: 'center',
        dataIndex: 'actions',
        render: (_, row) => (
            <TableActionsInline
                buttons={[
                    {
                        icon: <EyeOutlined />,
                        id: 'visualizar',
                        label: '',
                        onClick: () => open(row),
                        tooltip: 'Visualizar',
                    },
                ]}
            />
        ),

        title: 'Ação',
        width: '10%',
    },
];

export function LogsTable({
    filter,
    open,
    enabled = true,
}: {
    filter: Filter<LogFilter>;
    open: (row: Log) => void;
    enabled?: boolean;
}) {
    const screens = Grid.useBreakpoint();

    const baseParams: ApiLogGetParams = {
        data_fim: filter?.date_range?.[1]?.format(DATE_FORMAT_API),
        data_inicio: filter?.date_range?.[0]?.format(DATE_FORMAT_API),
        origem: filter?.log_origem,
        origem_id: filter?.log_origem_idpk,
        pesquisar: filter?.log_descricao,
        tipo: filter?.log_tipo?.join(','),
    };

    const { params, tablePagination, updateTotalAndPagination, onTableChange } = usePagination(
        baseParams,
        { order: 'log_data_insercao desc' },
    );

    const { data: logs, isFetching } = useQuery({
        enabled,
        placeholderData: keepPreviousData,
        queryFn: () => apiLogGet(params),
        queryKey: ['apiLogGet', params, filter],
    });

    useSyncPaginationTotal(updateTotalAndPagination, logs?.total_registros);

    return (
        <TableDefault
            columns={columns({ open, showDetails: !!screens.sm })}
            dataSource={logs?.registros}
            loading={isFetching}
            onChange={onTableChange}
            pagination={tablePagination}
            rowKey='log_idpk'
        />
    );
}
